import React, { useState } from "react";
import { Modal, Table, Row, Col, Typography, Tag, Input, Space, message } from "antd";
import { UiButton } from "../../ui";
import {
  approvePharmacyReturnItemApi,
  rejectPharmacyReturnItemApi,
} from "../../../services/pharmacyReturns.services";
import { formatAmount } from "../common/money";

const STATUS_COLOR = {
  requested: "orange",
  partially_approved: "gold",
  approved: "green",
  rejected: "red",
  pending: "orange",
};

const STATUS_LABEL = {
  requested: "Requested",
  partially_approved: "Partially Approved",
  approved: "Approved",
  rejected: "Rejected",
  pending: "Pending",
};

const ReturnDetailModal = ({ open, setOpen, returnRequest, refreshPage }) => {
  const [actioningItemId, setActioningItemId] = useState(null);
  const [rejectingItem, setRejectingItem] = useState(null);
  const [rejectRemarks, setRejectRemarks] = useState("");

  const itemLabel = (item) => item?.pharmacy_medicine_name || item?.medicine_name || "Item";

  const handleApprove = (item) => {
    setActioningItemId(item.id);
    approvePharmacyReturnItemApi(returnRequest.id, item.id)
      .then(() => {
        message.success(`${itemLabel(item)} approved, stock updated`);
        refreshPage();
      })
      .catch((err) => {
        message.error(err?.response?.data?.message || "Failed to approve item");
      })
      .finally(() => setActioningItemId(null));
  };

  const openRejectDialog = (item) => {
    setRejectingItem(item);
    setRejectRemarks("");
  };

  const handleReject = () => {
    if (!rejectRemarks.trim()) {
      message.warning("Please enter a reason for rejection");
      return;
    }
    setActioningItemId(rejectingItem.id);
    rejectPharmacyReturnItemApi(returnRequest.id, rejectingItem.id, { review_remarks: rejectRemarks })
      .then(() => {
        message.success(`${itemLabel(rejectingItem)} rejected`);
        setRejectingItem(null);
        refreshPage();
      })
      .catch((err) => {
        message.error(err?.response?.data?.message || "Failed to reject item");
      })
      .finally(() => setActioningItemId(null));
  };

  const itemColumns = [
    {
      title: "Medicine",
      key: "medicine",
      render: (_, item) => itemLabel(item),
    },
    { title: "Batch", dataIndex: "batch_number", key: "batch_number" },
    { title: "Qty", dataIndex: "quantity", key: "quantity" },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      render: (v) => formatAmount(v),
    },
    { title: "GST %", dataIndex: "gst_percentage", key: "gst_percentage" },
    { title: "Amount", dataIndex: "amount", key: "amount", render: (v) => formatAmount(v) },
    { title: "Reason", dataIndex: "reason", key: "reason" },
    {
      title: "Status",
      key: "status",
      render: (_, item) => (
        <div>
          <Tag color={STATUS_COLOR[item.status] || "default"}>
            {STATUS_LABEL[item.status] || item.status}
          </Tag>
          {item.status === "rejected" && item.review_remarks && (
            <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>{item.review_remarks}</div>
          )}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, item) =>
        item.status === "pending" ? (
          <Space>
            <UiButton
              size="sm"
              loading={actioningItemId === item.id}
              disabled={actioningItemId !== null && actioningItemId !== item.id}
              onClick={() => handleApprove(item)}
            >
              Approve
            </UiButton>
            <UiButton
              size="sm"
              variant="secondary"
              danger
              disabled={actioningItemId !== null}
              onClick={() => openRejectDialog(item)}
            >
              Reject
            </UiButton>
          </Space>
        ) : null,
    },
  ];

  return (
    <Modal
      title={`Return - ${returnRequest?.return_number || ""}`}
      open={open}
      onCancel={() => setOpen(false)}
      centered
      width={980}
      footer={null}
    >
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Typography.Text type="secondary">Bill Number</Typography.Text>
          <div>{returnRequest?.bill_number}</div>
        </Col>
        <Col span={6}>
          <Typography.Text type="secondary">Customer</Typography.Text>
          <div>{returnRequest?.patient_name || "-"}</div>
        </Col>
        <Col span={6}>
          <Typography.Text type="secondary">Total Amount</Typography.Text>
          <div>{formatAmount(returnRequest?.total_amount)}</div>
        </Col>
        <Col span={6}>
          <Typography.Text type="secondary">Status</Typography.Text>
          <div>
            <Tag color={STATUS_COLOR[returnRequest?.status] || "default"}>
              {STATUS_LABEL[returnRequest?.status] || returnRequest?.status}
            </Tag>
          </div>
        </Col>
      </Row>

      <Table
        dataSource={returnRequest?.items || []}
        columns={itemColumns}
        rowKey="id"
        pagination={false}
        scroll={{ x: "max-content" }}
      />

      {returnRequest?.remarks && (
        <div style={{ marginTop: 16 }}>
          <Typography.Text type="secondary">Remarks</Typography.Text>
          <div>{returnRequest.remarks}</div>
        </div>
      )}

      <Modal
        title={`Reject ${itemLabel(rejectingItem)}`}
        open={!!rejectingItem}
        onCancel={() => setRejectingItem(null)}
        centered
        width={420}
        okText="Save & Reject"
        okButtonProps={{ danger: true, loading: actioningItemId === rejectingItem?.id }}
        onOk={handleReject}
      >
        <Typography.Text>Reason for rejection</Typography.Text>
        <Input.TextArea
          rows={3}
          value={rejectRemarks}
          onChange={(e) => setRejectRemarks(e.target.value)}
          placeholder="e.g. Item was already opened / not eligible for return"
        />
      </Modal>
    </Modal>
  );
};

export default ReturnDetailModal;
